import * as React from "react";
import { ADLoginForm } from "./ADLoginForm";
import { LocalLoginForm } from "./LocalLoginForm";
import { StudentOrGuardianDialog } from "./StudentOrGuardianDialog";
import { TLCEvents } from "../events/events";
import { LS2PAC_CONSTANTS } from "../../main/constants";
import { useSupportedFunctionality } from "./SupportedFunctionality";
import { useLocationConfiguration } from "./LocationConfiguration";
import { useGlobalConfig } from "./GlobalConfig";
import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { TLCEventManager } from "../events/EventManager";
import { removeCookie, setCookie } from "typescript-cookie";
import { useWindowLocation } from "./WindowLocation";
import { ForgotPasswordDialogProvider } from "./ForgotPasswordDialog";
import { useStationLocation } from "./StationLocation";
import { useWindowVariables } from "./WindowVariables";
import { useLoginContext } from "./LoginContext";

export interface LoginDialogProps {
  isKids: boolean;
}

export interface LoginCredentials {
  username: string;
  password: string;
  rememberMe: boolean;
  localLoginOnly: boolean;
}

export const LoginDialog: React.FC<LoginDialogProps> = (props) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const theme = useTheme();
  const { loginMessage } = useWindowVariables();
  const { stationLocation } = useStationLocation();

  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { supportedFunctionality } = useSupportedFunctionality();
  const { navigate, url } = useWindowLocation();
  
  const { accountNotLinked: isAccountNotLinked, googleEmail: emailAddress, setAccountNotLinked, setGoogleEmail } = useLoginContext();
  
  const { locationConfiguration: { pacSettings } = { pacSettings: null } } =
    useLocationConfiguration();

  const { globalSettings, googleOauthEnabled, ldapEnabled, adfsSettings } =
    useGlobalConfig();

  const displayGuardianLogin = React.useMemo(() => {
    if (!pacSettings || !supportedFunctionality) {
      return false;
    }
    const { useGuardianProhibition, includeGuardiansInAD } =
      supportedFunctionality;
    const { displaySettings } = pacSettings;
    return (
      useGuardianProhibition === true &&
      includeGuardiansInAD === false &&
      displaySettings.displayGuardianLogin === true
    );
  }, [pacSettings, supportedFunctionality]);

  const [loginForm, setLoginForm] = React.useState<"AD" | "LOCAL" | "SSO">(
    null
  );

  React.useEffect(() => {
    if (adfsSettings) {

      if (!adfsSettings.enabled && isAccountNotLinked === true && emailAddress) {
        setOpen(true);
      }
    }
  }, [adfsSettings, isAccountNotLinked, emailAddress]);         

  const [showLoginError, setShowLoginError] = React.useState(false);
  const [defaultPassword, setDefaultPassword] = React.useState("");
  const [defaultUsername, setDefaultUsername] = React.useState("");

  const replaceURLParameters = () => {
    if (!url || url?.pathname === "/kids") {
      // parse kids URLs the old way as the hash doesn't follow a params pattern like LS2Pac
      const urlString = window.location.href.toString();
      var indexOfAccountNotLinked = urlString.indexOf("&accountNotLinked=true");
      if (indexOfAccountNotLinked !== -1) {
        return urlString.substring(0, indexOfAccountNotLinked);
      }
      return urlString;
    }

    const params = new URLSearchParams(url.hash.slice(1));

    if (params.get("section") === "obrsignup") {
      params.set("section", "home");
    }

    if (params.get("section") === "resetPIN") {
      params.set("section", "home");
    }

    if (params.has("accountNotLinked")) {
      params.delete("accountNotLinked");
    }

    if (params.size === 0) {
      params.set("section", "home");
    }

    url.hash = params.toString();
    return url.toString();
  };

  const adfsRedirect = React.useMemo(() => {
    console.log("Recalculating adfsRedirect");
    if (!url || !adfsSettings?.enabled) {
      return null;
    }
    const hash = url.hash.substring(1).replace(new RegExp("%22", "g"), '"');
    return (
      adfsSettings.loginUrl +
      "?path=" +
      encodeURIComponent(url.pathname) +
      "&search=" +
      encodeURIComponent(url.search) +
      "&hash=" +
      encodeURIComponent(hash)
    );
  }, [url, adfsSettings]);

  React.useEffect(() => {
    const handlePromptForLogin = (redirectURL: string) => {
      if (redirectURL) {
        setCookie("SAVED_DESTINATION", redirectURL);
        console.log("SAVED_DESTINATION", redirectURL);
      } else {
        const locationToSave = replaceURLParameters();
        setCookie("SAVED_DESTINATION", locationToSave);
        console.log("SAVED_DESTINATION", locationToSave);
      }
      if (adfsSettings?.enabled && !displayGuardianLogin) {
        navigate(adfsRedirect);
        return;
      }
      setOpen(true);
    };
    // TODO type interface
    const handleLoginResponse = (event: any) => {
      if (event.success) {
        onClose(true);
      } else {
        setShowLoginError(true);
      }
    };

    const promptEventGuid = TLCEvents.on(
      LS2PAC_CONSTANTS.Events.onPromptForLogin,
      (event: { redirectURL?: string }) => {
        handlePromptForLogin(event.redirectURL);
      }
    );
    const responseEventGuid = TLCEvents.on(
      LS2PAC_CONSTANTS.Events.onLoginResponse,
      handleLoginResponse
    );

    return () => {
      TLCEvents.off(LS2PAC_CONSTANTS.Events.onPromptForLogin, promptEventGuid);
      TLCEvents.off(LS2PAC_CONSTANTS.Events.onLoginResponse, responseEventGuid);
    };
  }, [adfsSettings, displayGuardianLogin, url]);

  React.useEffect(() => {
    if (open && adfsSettings) {

      // there is no sign in with google in an adfs setup
      if (adfsSettings.enabled) {
        setLoginForm("SSO");
        return;
      }

      if (isAccountNotLinked) {
        setLoginForm("LOCAL");
        return;
      }

      if (ldapEnabled) {
        setLoginForm("AD");
        return;
      }

      setLoginForm("LOCAL");
    }

    return () => {};
  }, [ldapEnabled, adfsSettings, isAccountNotLinked, open]);

  const handleLogin = (creds: LoginCredentials) => {
    TLCEventManager.fireEvent(LS2PAC_CONSTANTS.Events.onLogin, {
      credentials: creds,
    });
  };

  const handleClose = (event: React.SyntheticEvent, reason: string) => {
    if (reason === "backdropClick") {
      event.preventDefault();
      return;
    }
    onClose();
  };

  const onClose = (closedViaSubmit: boolean = false) => {
    setOpen(false);
    setShowLoginError(false);
    setDefaultPassword("");
    setDefaultUsername("");
    removeCookie("SAVED_DESTINATION");
    TLCEventManager.fireEvent(LS2PAC_CONSTANTS.Events.onCloseLogin, {
      closedViaSubmit,
    });
  };

  const showRememberMe =
    pacSettings?.displaySettings.showRememberMe && !stationLocation;

  return (
    <React.Fragment>
      {/* use open to conditionally render and reset initial state */}
      {open && loginForm !== "SSO" && (
        <Dialog open={open} onClose={handleClose} fullScreen={fullScreen}>
          {loginForm === "AD" && (
            <ADLoginForm
              showLoginError={showLoginError}
              loginMessage={loginMessage}
              onSubmitLogin={handleLogin}
              onClickClose={onClose}
              defaultPassword={defaultPassword}
              defaultUsername={defaultUsername}
              displayGuardianLogin={displayGuardianLogin}
              {...props}
              onClickGuardianLogin={({ password, barcode }) => {
                setDefaultPassword(password);
                setDefaultUsername(barcode);
                setShowLoginError(false);
                setLoginForm("LOCAL");
              }}
            ></ADLoginForm>
          )}
          {globalSettings && pacSettings && loginForm === "LOCAL" && (
            <ForgotPasswordDialogProvider>
              <LocalLoginForm
                displayGuardianLogin={
                  (adfsSettings?.enabled || ldapEnabled) && displayGuardianLogin
                }
                showLoginError={showLoginError}
                loginMessage={loginMessage}
                onSubmitLogin={handleLogin}
                onClickClose={onClose}
                defaultPassword={defaultPassword}
                defaultUsername={defaultUsername}
                onClickBack={({ barcode, password }) => {
                  setLoginForm(ldapEnabled ? "AD" : "SSO");
                  setShowLoginError(false);
                  if (ldapEnabled) {
                    setDefaultPassword(password);
                    setDefaultUsername(barcode);
                  }
                }}
                isGoogleLoginEnabled={
                  pacSettings.googleSSOEnabled &&
                  googleOauthEnabled &&
                  !adfsSettings?.enabled
                }
                accountNotLinked={isAccountNotLinked ? emailAddress : null}
                showRememberMe={showRememberMe}
                useForgotPasswordLink={globalSettings.useForgotPasswordLink}
                showResetPINLink={pacSettings.displaySettings.showResetPINLink}
                {...props}
              ></LocalLoginForm>
            </ForgotPasswordDialogProvider>
          )}
        </Dialog>
      )}
      {open && loginForm === "SSO" && displayGuardianLogin && (
        <StudentOrGuardianDialog
          onClickGuardianLogin={() => setLoginForm("LOCAL")}
          onClickStudentLogin={() => navigate(adfsRedirect)}
        ></StudentOrGuardianDialog>
      )}
    </React.Fragment>
  );
};
